import { useState, useEffect, useCallback } from "react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const MAX_RETRIES = 5;
const POLL_INTERVAL = 5000;
const XHR_TIMEOUT = 15000;

export function useReportData(reportId) {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);

  const retry = useCallback(() => setRetryCount((c) => c + 1), []);

  useEffect(() => {
    let timeoutId = null;
    let cancelled = false;
    let failCount = 0;
    let activeXhr = null;

    setError(null);
    setLoading(true);

    const fetchReport = () => {
      if (cancelled) return;
      const xhr = new XMLHttpRequest();
      activeXhr = xhr;
      xhr.open("GET", `${API}/reports/${reportId}`);
      xhr.setRequestHeader("Accept", "application/json");
      xhr.timeout = XHR_TIMEOUT;

      xhr.onload = () => {
        if (cancelled) return;
        if (xhr.status === 404) {
          setError("Report not found");
          setLoading(false);
          return;
        }
        if (xhr.status < 200 || xhr.status >= 300) {
          failCount++;
          if (failCount >= MAX_RETRIES) {
            setError("Unable to load report. Please refresh the page to try again.");
            setLoading(false);
            return;
          }
          timeoutId = setTimeout(fetchReport, POLL_INTERVAL);
          return;
        }
        try {
          const data = JSON.parse(xhr.responseText);
          if (cancelled) return;
          failCount = 0;
          setReport(data);
          setLoading(false);
          if (data.status === "processing" || data.status === "pending") {
            timeoutId = setTimeout(fetchReport, POLL_INTERVAL);
          }
        } catch (parseErr) {
          console.warn("Failed to parse report response:", parseErr.message);
          failCount++;
          if (failCount >= MAX_RETRIES) {
            setError("Unable to load report. Please refresh the page to try again.");
            setLoading(false);
            return;
          }
          timeoutId = setTimeout(fetchReport, POLL_INTERVAL);
        }
      };

      xhr.onerror = () => {
        if (cancelled) return;
        failCount++;
        if (failCount >= MAX_RETRIES) {
          setError("Unable to load report. Please refresh the page to try again.");
          setLoading(false);
          return;
        }
        timeoutId = setTimeout(fetchReport, POLL_INTERVAL);
      };

      xhr.ontimeout = () => {
        if (cancelled) return;
        failCount++;
        if (failCount >= MAX_RETRIES) {
          setError("Unable to load report. Please refresh the page to try again.");
          setLoading(false);
          return;
        }
        timeoutId = setTimeout(fetchReport, POLL_INTERVAL);
      };

      xhr.send();
    };

    fetchReport();

    return () => {
      cancelled = true;
      if (timeoutId) clearTimeout(timeoutId);
      if (activeXhr) {
        try { activeXhr.abort(); } catch { /* ignore */ }
      }
    };
  }, [reportId, retryCount]);

  return { report, loading, error, retry };
}
